import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ResidentReviews = ({ commune }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  if (!commune) return null;

  const reviewCategories = [
  { id: 'all', label: 'Tous les Avis', count: commune?.reviews?.length },
  { id: 'families', label: 'Familles', count: commune?.reviews?.filter((r) => r?.category === 'families')?.length },
  { id: 'professionals', label: 'Professionnels', count: commune?.reviews?.filter((r) => r?.category === 'professionals')?.length },
  { id: 'students', label: 'Étudiants', count: commune?.reviews?.filter((r) => r?.category === 'students')?.length },
  { id: 'seniors', label: 'Seniors', count: commune?.reviews?.filter((r) => r?.category === 'seniors')?.length }];


  const filteredReviews = selectedCategory === 'all' ?
  commune?.reviews :
  commune?.reviews?.filter((review) => review?.category === selectedCategory);

  const averageRating = commune?.reviews?.reduce((sum, review) => sum + review?.rating, 0) / commune?.reviews?.length;

  const ratingDistribution = [5, 4, 3, 2, 1]?.map((rating) => ({
    rating,
    count: commune?.reviews?.filter((r) => r?.rating === rating)?.length,
    percentage: commune?.reviews?.filter((r) => r?.rating === rating)?.length / commune?.reviews?.length * 100
  }));

  return (
    <div className="bg-white border-none">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-16 border-none">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="MessageSquare" size={20} className="text-brand-gold" />
            <span className="text-sm font-medium text-brand-gold uppercase tracking-wide">
              Avis des Résidents
            </span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-4">
            Ce que disent nos résidents
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Découvrez les témoignages authentiques de ceux qui vivent à {commune?.name}
          </p>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Rating Overview */}
          <div className="lg:col-span-1">
            <div className="bg-surface rounded-xl p-6 sticky top-24 border-none">
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-text-primary mb-2">
                  {averageRating?.toFixed(1)}
                </div>
                <div className="flex items-center justify-center space-x-1 mb-2">
                  {[1, 2, 3, 4, 5]?.map((star) =>
                  <Icon
                    key={star}
                    name="Star"
                    size={16}
                    className={star <= Math.round(averageRating) ? 'text-yellow-500' : 'text-gray-300'} />

                  )}
                </div>
                <p className="text-sm text-text-secondary">
                  Basé sur {commune?.reviews?.length} avis
                </p>
              </div>

              {/* Rating Distribution */}
              <div className="space-y-2 mb-6">
                {ratingDistribution?.map((item) =>
                <div key={item?.rating} className="flex items-center space-x-2">
                    <span className="text-sm text-text-secondary w-2">{item?.rating}</span>
                    <Icon name="Star" size={12} className="text-yellow-500" />
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                      className="bg-brand-gold h-2 rounded-full"
                      style={{ width: `${item?.percentage}%` }} />

                    </div>
                    <span className="text-xs text-text-secondary w-8">{item?.count}</span>
                  </div>
                )}
              </div>

              {/* Category Filter */}
              <div className="space-y-2">
                <h4 className="font-semibold text-text-primary text-sm mb-3">
                  Filtrer par profil
                </h4>
                {reviewCategories?.map((category) =>
                <button
                  key={category?.id}
                  onClick={() => setSelectedCategory(category?.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors duration-300 ${
                  selectedCategory === category?.id ?
                  'bg-brand-gold text-white' : 'text-text-secondary hover:bg-gray-100'}`
                  }>

                    <div className="flex items-center justify-between">
                      <span>{category?.label}</span>
                      <span className="text-xs">{category?.count}</span>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Reviews List */}
          <div className="lg:col-span-3">
            <div className="space-y-6">
              {filteredReviews?.map((review, index) =>
              <div key={index} className="bg-surface rounded-xl p-6 border border-border hover:shadow-warm transition-shadow duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <Image
                      src={review?.avatar}
                      alt={review?.name}
                      className="w-12 h-12 rounded-full object-cover" />

                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-text-primary">{review?.name}</h4>
                          <p className="text-sm text-text-secondary">{review?.profile}</p>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <div className="flex items-center space-x-1">
                            {[1, 2, 3, 4, 5]?.map((star) =>
                          <Icon
                            key={star}
                            name="Star"
                            size={14}
                            className={star <= review?.rating ? 'text-yellow-500' : 'text-gray-300'} />

                          )}
                          </div>
                          <span className="text-xs text-text-secondary">{review?.date}</span>
                        </div>
                      </div>
                      
                      <p className="text-text-secondary mb-4 leading-relaxed">
                        {review?.comment}
                      </p>
                      
                      {/* Review Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {review?.tags?.map((tag, tagIndex) =>
                      <span
                        key={tagIndex}
                        className="px-2 py-1 bg-white rounded-full text-xs text-text-secondary border border-border">

                            {tag}
                          </span>
                      )}
                      </div>
                      
                      {/* Helpful Actions */}
                      <div className="flex items-center space-x-4">
                        <button className="flex items-center space-x-1 text-sm text-text-secondary hover:text-brand-gold transition-colors duration-300">
                          <Icon name="ThumbsUp" size={14} />
                          <span>Utile ({review?.helpful})</span>
                        </button>
                        
                        <button className="flex items-center space-x-1 text-sm text-text-secondary hover:text-brand-gold transition-colors duration-300">
                          <Icon name="MessageCircle" size={14} />
                          <span>Répondre</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Load More */}
            <div className="text-center mt-8">
              <Button variant="outline" iconName="ChevronDown" iconPosition="right">
                Voir plus d'avis
              </Button>
            </div>
          </div>
        </div>

        {/* Add Review CTA */}
        <div className="mt-16 bg-gradient-to-br from-brand-gold/5 to-brand-earth/5 rounded-2xl p-8 border border-brand-gold/20 text-center border-none">
          <Icon name="Edit3" size={32} className="text-brand-gold mx-auto mb-4" />
          <h3 className="text-2xl font-heading font-bold text-text-primary mb-2">
            Vous habitez à {commune?.name} ?
          </h3>
          <p className="text-text-secondary mb-6 max-w-2xl mx-auto">
            Partagez votre expérience pour aider d'autres personnes à découvrir votre quartier
          </p>
          
          <Button variant="default" size="lg" iconName="Plus" iconPosition="left">
            Écrire un Avis
          </Button>
        </div>
      </div>
    </div>);

};

export default ResidentReviews;